import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "./AuthContext";

const CartContext = createContext({});

// Guest cart still uses localStorage — synced up to the account on next login.
const STORAGE_KEY = "bookfreely.cart.v2";

function loadGuest() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function saveGuest(items) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* localStorage disabled */ }
}

export function CartProvider({ children }) {
  const { user, authHeader } = useAuth();
  const [cart, setCart] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const mergedRef = useRef(false); // guard against re-merging guest items every render

  const isCustomerRole = !user || user.role === "customer" || user.role === "super_admin";

  const refresh = useCallback(async () => {
    if (!user) {
      // Guest — read localStorage
      setCart(loadGuest());
      setSaved([]);
      return;
    }
    if (!isCustomerRole) { setCart([]); setSaved([]); return; }
    try {
      setLoading(true);
      const r = await axios.get(`${API}/cart`, { headers: authHeader() });
      setCart(r.data.cart || []);
      setSaved(r.data.saved || []);
    } catch (e) {
      // 401 shouldn't happen since we gate on `user`, but be defensive.
      if (e?.response?.status !== 401) {
        console.error("cart refresh failed", e);
      }
    } finally {
      setLoading(false);
    }
  }, [user, authHeader, isCustomerRole]);

  // On login → merge any guest items THEN refresh from server.
  useEffect(() => {
    if (!user) { mergedRef.current = false; refresh(); return; }
    if (!isCustomerRole) { setCart([]); setSaved([]); return; }
    if (mergedRef.current) return;
    (async () => {
      const guest = loadGuest();
      if (guest && guest.length) {
        try {
          const items = guest.map((g) => ({ service_id: g.service_id, scheduled_at: g.scheduled_at, notes: g.notes || null }));
          await axios.post(`${API}/cart/merge`, { items }, { headers: authHeader() });
          saveGuest([]); // clear once merged
        } catch (e) {
          console.error("cart merge failed", e);
        }
      }
      mergedRef.current = true;
      refresh();
    })();
  }, [user, isCustomerRole, authHeader, refresh]);

  // Cross-tab sync for guests.
  useEffect(() => {
    if (user) return;
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setCart(loadGuest()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [user]);

  const add = useCallback(async (item, { onNeedAuth } = {}) => {
    if (!item?.service_id || !item?.scheduled_at) {
      toast.error("Pick a date and time first");
      return { ok: false, reason: "missing-slot" };
    }
    if (!user) {
      // Guest: push to localStorage, cap at 20.
      const existing = loadGuest();
      const dupe = existing.some((p) => p.service_id === item.service_id && p.scheduled_at === item.scheduled_at);
      if (dupe) { toast.info("This time slot is already in your cart"); return { ok: false, reason: "duplicate" }; }
      const next = [...existing, item].slice(-20);
      saveGuest(next);
      setCart(next);
      toast.success(`Added "${item.service_title}" to cart`);
      // The service page (or a modal) can prompt sign-in via onNeedAuth to persist across devices.
      if (onNeedAuth) onNeedAuth();
      return { ok: true, guest: true };
    }
    if (!isCustomerRole) {
      toast.error("Only customers can add services to a cart");
      return { ok: false, reason: "wrong-role" };
    }
    try {
      const r = await axios.post(`${API}/cart/items`, {
        service_id: item.service_id, scheduled_at: item.scheduled_at, notes: item.notes || null,
      }, { headers: authHeader() });
      if (r.data.duplicated) {
        toast.info(r.data.list === "saved" ? "Already in your Saved for Later list" : "This time slot is already in your cart");
      } else {
        toast.success(`Added "${item.service_title}" to cart`);
      }
      await refresh();
      return { ok: true, guest: false, duplicated: !!r.data.duplicated };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to cart");
      return { ok: false, reason: "server-error" };
    }
  }, [user, isCustomerRole, authHeader, refresh]);

  const remove = useCallback(async (item_id) => {
    if (!user) {
      const next = loadGuest().filter((p) => `${p.service_id}|${p.scheduled_at}` !== item_id);
      saveGuest(next);
      setCart(next);
      return;
    }
    try {
      await axios.delete(`${API}/cart/items/${item_id}`, { headers: authHeader() });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  }, [user, authHeader, refresh]);

  const saveForLater = useCallback(async (item_id) => {
    if (!user) { toast.info("Sign in to save items for later"); return; }
    try {
      await axios.post(`${API}/cart/items/${item_id}/save-for-later`, {}, { headers: authHeader() });
      toast.success("Moved to Saved for Later");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save item");
    }
  }, [user, authHeader, refresh]);

  const moveToCart = useCallback(async (item_id) => {
    if (!user) return;
    try {
      await axios.post(`${API}/cart/items/${item_id}/move-to-cart`, {}, { headers: authHeader() });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not move to cart");
    }
  }, [user, authHeader, refresh]);

  const clear = useCallback(async () => {
    if (!user) { saveGuest([]); setCart([]); return; }
    try {
      const ids = cart.map((c) => c.id);
      await Promise.all(ids.map((id) => axios.delete(`${API}/cart/items/${id}`, { headers: authHeader() })));
      await refresh();
    } catch (e) {
      toast.error("Could not clear cart");
    }
  }, [user, cart, authHeader, refresh]);

  const revalidate = useCallback(async () => {
    if (!user) return { changes: [], ok: true };
    try {
      const r = await axios.post(`${API}/cart/revalidate`, {}, { headers: authHeader() });
      // Refresh so any auto-removed items disappear from the UI.
      await refresh();
      return r.data;
    } catch (e) {
      toast.error("Could not revalidate cart");
      return { changes: [], ok: false };
    }
  }, [user, authHeader, refresh]);

  // Guest-mode items expose a synthetic id for delete calls.
  const items = useMemo(() => {
    if (user) return cart;
    return cart.map((c) => ({ ...c, id: `${c.service_id}|${c.scheduled_at}` }));
  }, [cart, user]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + (Number(i.price) || 0), 0);
    const totalDeposit = items.reduce((s, i) => s + (Number(i.deposit) || 0), 0);
    const depositItemsFullPrice = items.reduce(
      (s, i) => s + (Number(i.deposit) > 0 ? (Number(i.price) || 0) : 0), 0,
    );
    const businesses = new Set(items.map((i) => i.business_id));
    return {
      count: items.length,
      subtotal: +subtotal.toFixed(2),
      totalDeposit: +totalDeposit.toFixed(2),
      depositItemsFullPrice: +depositItemsFullPrice.toFixed(2),
      uniqueBusinesses: businesses.size,
      hasDepositItems: items.some((i) => Number(i.deposit) > 0),
    };
  }, [items]);

  return (
    <CartContext.Provider value={{
      items, saved, totals, loading,
      add, remove, clear, saveForLater, moveToCart, revalidate, refresh,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() { return useContext(CartContext); }
